/**
 * Organizations List Page
 *
 * Displays a list of all organizations with search, filtering, and pagination.
 */

import { useState, useCallback, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, Plus } from 'lucide-react';
import type { ColumnDef, SortingState } from '@tanstack/react-table';

import {
  DataTable,
  Button,
  Badge,
  Card,
  CardHeader,
  CardBody,
} from '@/components/common';
import { organizationService } from '@/api/services';
import type { Organization, ListRequest } from '@/api/types';
import { formatDate } from '@/utils/date.utils';
import { useToast } from '@/hooks/useToast';
import { CreateOrganizationModal } from './CreateOrganizationModal';

// ============================================
// Page Component
// ============================================

export function OrganizationsListPage() {
  const navigate = useNavigate();
  const toast = useToast();

  // State
  const [data, setData] = useState<Organization[]>([]);
  const [totalRows, setTotalRows] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });
  const [sorting, setSorting] = useState<SortingState>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  // Fetch data
  const fetchOrganizations = useCallback(
    async (params: { pageIndex: number; pageSize: number; sorting?: SortingState; search?: string }) => {
      setIsLoading(true);
      try {
        const request: ListRequest = {
          paging: {
            page: params.pageIndex + 1,
            size: params.pageSize,
          },
          sorting:
            params.sorting && params.sorting.length > 0
              ? { [params.sorting[0].id]: params.sorting[0].desc ? 'desc' : 'asc' }
              : undefined,
          filtering: params.search ? { name: params.search } : undefined,
        };

        const result = await organizationService.getOrganizationList(request);
        setData(result.list);
        // Use filteredRecords for pagination (reflects search/filter results)
        setTotalRows(result.filteredRecords);
      } catch (error) {
        console.error('Failed to fetch organizations:', error);
        toast.error('Failed to load organizations');
      } finally {
        setIsLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  // Handle pagination change
  const handlePaginationChange = useCallback(
    (newPagination: { pageIndex: number; pageSize: number }) => {
      setPagination(newPagination);
    },
    []
  );

  // Handle sorting change
  const handleSortingChange = useCallback((newSorting: SortingState) => {
    setSorting(newSorting);
  }, []);

  // Handle search change
  const handleSearchChange = useCallback(
    (value: string) => {
      setSearchTerm(value);
      // Reset to first page when searching
      setPagination((prev) => (prev.pageIndex === 0 ? prev : { ...prev, pageIndex: 0 }));
    },
    []
  );

  // Debounce search term
  useEffect(() => {
    // If search is cleared, update immediately
    if (searchTerm === '') {
      setDebouncedSearchTerm('');
      return;
    }

    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Fetch data when pagination, sorting, or debounced search changes
  useEffect(() => {
    fetchOrganizations({ ...pagination, sorting, search: debouncedSearchTerm });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination, sorting, debouncedSearchTerm]);

  // Navigate to details
  const handleViewDetails = useCallback(
    (org: Organization) => {
      navigate(`/organizations/${org.id}`);
    },
    [navigate]
  );

  // Column definitions
  const columns = useMemo<ColumnDef<Organization>[]>(
    () => [
      {
        accessorKey: 'code',
        header: 'Code',
        cell: ({ row }) => (
          <span className="font-medium text-foreground dark:text-foreground-dark">
            {row.original.code}
          </span>
        ),
      },
      {
        accessorKey: 'name',
        header: 'Organization Name',
        cell: ({ row }) => (
          <div className="flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary-100 dark:bg-primary-900/30">
              <Building2 className="h-4 w-4 text-primary" />
            </div>
            <span className="font-medium">{row.original.name}</span>
          </div>
        ),
      },
      {
        accessorKey: 'isPayrollSpecific',
        header: 'Payroll Specific',
        cell: ({ row }) => (
          <span className={row.original.isPayrollSpecific ? 'text-success' : 'text-foreground-muted dark:text-foreground-dark-muted'}>
            {row.original.isPayrollSpecific ? 'Yes' : 'No'}
          </span>
        ),
      },
      {
        accessorKey: 'isMultipleUnitsAllowed',
        header: 'Multiple Units',
        cell: ({ row }) => (
          <span className={row.original.isMultipleUnitsAllowed ? 'text-success' : 'text-foreground-muted dark:text-foreground-dark-muted'}>
            {row.original.isMultipleUnitsAllowed ? 'Yes' : 'No'}
          </span>
        ),
      },
      {
        accessorKey: 'status',
        header: 'Status',
        cell: ({ row }) => {
          const status = row.original.status;
          const variant =
            status === 'ACTIVE'
              ? 'success'
              : status === 'SUSPENDED'
                ? 'warning'
                : 'default';
          return (
            <Badge variant={variant} dot>
              {status}
            </Badge>
          );
        },
      },
      {
        accessorKey: 'addedDate',
        header: 'Created Date',
        cell: ({ row }) => (
          <span className="text-foreground-muted dark:text-foreground-dark-muted">
            {formatDate(row.original.addedDate)}
          </span>
        ),
      },
    ],
    []
  );

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground dark:text-foreground-dark">
            Organizations
          </h1>
          <p className="mt-1 text-sm text-foreground-muted dark:text-foreground-dark-muted">
            Manage and view all organizations in the platform
          </p>
        </div>
        <Button
          leftIcon={<Plus className="h-4 w-4" />}
          onClick={() => setIsCreateModalOpen(true)}
        >
          Create Organization
        </Button>
      </div>

      {/* Data Table */}
      <Card>
        <CardHeader
          title="Organization List"
          description="Click on a row to view details"
        />
        <CardBody className="p-0">
          <DataTable
            columns={columns}
            data={data}
            isLoading={isLoading}
            searchable
            searchPlaceholder="Search organizations..."
            searchValue={searchTerm}
            onSearchChange={handleSearchChange}
            paginated
            totalRows={totalRows}
            pageIndex={pagination.pageIndex}
            pageSize={pagination.pageSize}
            onPaginationChange={handlePaginationChange}
            sortingState={sorting}
            onSortingChange={handleSortingChange}
            onRowClick={handleViewDetails}
            emptyMessage="No organizations found"
            emptyDescription="There are no organizations registered yet."
            className="border-0"
          />
        </CardBody>
      </Card>

      {/* Create Organization Modal */}
      <CreateOrganizationModal
        open={isCreateModalOpen}
        onOpenChange={setIsCreateModalOpen}
        onSuccess={() => {
          fetchOrganizations({ ...pagination, sorting, search: searchTerm });
        }}
      />
    </div>
  );
}

export default OrganizationsListPage;
